<div class="pinned">
  <div class="title">
    <h1>{{ 'platform.overview.title' | translate }}</h1>
  </div>
  <div class="buttons">
    <label class="count" *ngIf="count != null">
      {{ count }} {{ (count === 1 ? 'platform.overview.org_one' : 'platform.overview.org_other') | translate }}
    </label>

    <nb-select size="small" [(selected)]="selectedMonthNum" (selectedChange)="onMonthChange()" aria-label="Month">
      <nb-option *ngFor="let m of months" [value]="m.value">{{ m.label }}</nb-option>
    </nb-select>

    <nb-select size="small" [(selected)]="selectedYear" (selectedChange)="onMonthChange()" aria-label="Year">
      <nb-option *ngFor="let y of years" [value]="y">{{ y }}</nb-option>
    </nb-select>

    <bst-table-options-button [count]="filterCount" (open)="events.next(DualAxisEvent.OPENFILTERS)"></bst-table-options-button>

    <button nbButton size="small" status="primary" shape="semi-round"
            (click)="downloadCsv()"
            [disabled]="(loading | async) || !data.length">
      <nb-icon icon="download-outline"></nb-icon> {{ 'platform.overview.download_csv' | translate }}
    </button>
  </div>
</div>

<div class="timeline-block" *ngIf="!errorMessage">
  <div class="timeline-controls">
    <button
      *ngFor="let w of [6, 12, 24]"
      class="window-pill"
      [class.active]="selectedWindow === w"
      (click)="onWindowSelect(w)"
      type="button">
      {{ w }}mo
    </button>
  </div>
  <bst-area-chart
    *ngIf="timeline"
    [months]="timeline.months"
    [values]="timeline.platform_total">
  </bst-area-chart>
</div>

<div *ngIf="errorMessage" class="error-banner">{{ errorMessage }}</div>
<p class="intake-prune" *ngIf="!errorMessage">{{ pruneLine }}</p>

<p class="page-caveat">
  Status badges (active / dormant / inactive) reflect <em>current</em> login
  recency relative to today, not the org's status at the end of the
  selected month. Active-profile count = every seat in the tenant's Users tab
  that isn't archived (the billing population). Logged-in users = subset who
  have actually signed in at least once.
</p>

<div class="kpi-strip" *ngIf="kpiCards.length">
  <div class="kpi-tile" *ngFor="let kpi of kpiCards">
    <div class="kpi-label">{{ kpi.label }}</div>
    <div class="kpi-value">{{ kpi.value }}</div>
    <div class="kpi-trend"
         [class.up]="kpi.delta != null && kpi.delta > 0"
         [class.down]="kpi.delta != null && kpi.delta < 0"
         [class.flat]="kpi.delta != null && kpi.delta === 0"
         *ngIf="kpi.deltaLabel">
      {{ kpi.deltaLabel }} <span class="kpi-trend-context">vs {{ prevMonthLabel }}</span>
    </div>
  </div>
</div>

<bst-dual-axis [tag]="'platform-overview'" [delegate]="this"></bst-dual-axis>

<ng-template #cellTemplate let-row="row" let-column="column" let-node="node">
  <ng-container [ngSwitch]="column.name">

    <div *ngSwitchCase="'organization'" class="org-cell">
      <div class="org-logo">
        <img *ngIf="row.logo_url" [src]="row.logo_url" [alt]="row.client_name">
        <span *ngIf="!row.logo_url" [style.background]="colorOf(row.client_name)">{{ initialsOf(row.client_name) }}</span>
      </div>
      <div class="org-text">
        <div class="org-name">
          {{ row.client_name }}
          <span class="org-status" [class]="'is-' + row.status">{{ row.status }}</span>
        </div>
        <div class="org-domain">{{ row.client_domain || '-' }}</div>
      </div>
    </div>

    <div *ngSwitchCase="'active_profile_count'" class="num">{{ formatNumber(row.active_profile_count) }}</div>
    <div *ngSwitchCase="'profiles_trend'" class="trend-cell">
      <bst-sparkline [values]="trendFor(row)"></bst-sparkline>
    </div>
    <div *ngSwitchCase="'logged_in_users'"      class="num">{{ formatNumber(row.logged_in_users) }}</div>
    <div *ngSwitchCase="'storage_bytes'"        class="num">{{ formatBytes(row.storage_bytes) }}</div>
    <div *ngSwitchCase="'matching_events'"      class="num">{{ formatNumber(row.matching_events) }}</div>
    <div *ngSwitchCase="'last_login_at'"        class="num">{{ formatDate(row.last_login_at) }}</div>
    <div *ngSwitchCase="'intake_enabled'">
      <nb-toggle [checked]="!!row.intake_enabled" [disabled]="isIntakeBusy(row)"
                 (checkedChange)="toggleIntake(row, $event)"
                 [attr.aria-label]="'platform.overview.intake.col_enabled' | translate"></nb-toggle>
    </div>
    <div *ngSwitchCase="'quotes_enabled'">
      <nb-toggle [checked]="!!row.quotes_enabled" [disabled]="isIntakeBusy(row)"
                 (checkedChange)="toggleQuotes(row, $event)"
                 [attr.aria-label]="'platform.overview.quotes.col_enabled' | translate"></nb-toggle>
    </div>
    <!-- Rotate and Revoke sit under the address: a column of their own pushed the table past the page. -->
    <div *ngSwitchCase="'intake_address_masked'" class="intake-address">
      <span class="masked">{{ addressText(row) }}</span>
      <div *ngIf="canRotate(row)" class="intake-actions">
        <button nbButton size="tiny" status="basic" [disabled]="isIntakeBusy(row)" (click)="rotateIntake(row)">
          {{ 'platform.overview.intake.action_rotate' | translate }}
        </button>
        <button *ngIf="canRevoke(row)" nbButton size="tiny" status="danger" ghost [disabled]="isIntakeBusy(row)" (click)="revokeIntake(row)">
          {{ 'platform.overview.intake.action_revoke' | translate }}
        </button>
      </div>
    </div>
    <div *ngSwitchCase="'intake_month_mails'" class="num">
      <ng-container *ngIf="row.intake_month_mails != null; else dash">
        {{ formatNumber(row.intake_month_mails) }}
        <span class="hint">{{ 'platform.overview.intake.requests' | translate: { n: formatNumber(row.intake_month_direct ?? 0) } }}</span>
      </ng-container>
    </div>
    <div *ngSwitchCase="'intake_monthly_cap'" class="intake-cap">
      <ng-container *ngIf="row.intake_address_masked; else dash">
        <input nbInput fieldSize="small" type="text" inputmode="numeric" [(ngModel)]="capDrafts[row.client_id]"
               [placeholder]="'platform.overview.intake.default_cap' | translate" [disabled]="isIntakeBusy(row)"
               [attr.aria-label]="'platform.overview.intake.col_cap' | translate">
        <button nbButton size="tiny" status="basic" [disabled]="isIntakeBusy(row) || !capDirty(row)" (click)="saveCap(row)">
          {{ 'platform.overview.intake.save' | translate }}
        </button>
      </ng-container>
    </div>
    <div *ngSwitchCase="'intake_held'" class="num">
      <ng-container *ngIf="row.intake_held != null; else dash">{{ formatNumber(row.intake_held) }}</ng-container>
    </div>

  </ng-container>
</ng-template>

<!-- Mobile (#445): stacked card per org. dual-axis swaps the 7-column table for this below 768px. -->
<ng-template #cardTemplate let-row="row" let-node="node">
  <div class="org-card">
    <div class="org-cell">
      <div class="org-logo">
        <img *ngIf="row.logo_url" [src]="row.logo_url" [alt]="row.client_name">
        <span *ngIf="!row.logo_url" [style.background]="colorOf(row.client_name)">{{ initialsOf(row.client_name) }}</span>
      </div>
      <div class="org-text">
        <div class="org-name">
          {{ row.client_name }}
          <span class="org-status" [class]="'is-' + row.status">{{ row.status }}</span>
        </div>
        <div class="org-domain">{{ row.client_domain || '-' }}</div>
      </div>
    </div>

    <bst-sparkline *ngIf="trendFor(row).length" [values]="trendFor(row)"></bst-sparkline>

    <dl class="org-card-metrics">
      <div><dt>{{ 'profiles' | translate }}</dt><dd>{{ formatNumber(row.active_profile_count) }}</dd></div>
      <div><dt>{{ 'loggedinusers' | translate }}</dt><dd>{{ formatNumber(row.logged_in_users) }}</dd></div>
      <div><dt>{{ 'storage' | translate }}</dt><dd>{{ formatBytes(row.storage_bytes) }}</dd></div>
      <div><dt>{{ 'matching' | translate }}</dt><dd>{{ formatNumber(row.matching_events) }}</dd></div>
      <div><dt>{{ 'lastlogin' | translate }}</dt><dd>{{ formatDate(row.last_login_at) }}</dd></div>
      <div><dt>{{ 'platform.overview.intake.col_enabled' | translate }}</dt>
        <dd><nb-toggle [checked]="!!row.intake_enabled" [disabled]="isIntakeBusy(row)" (checkedChange)="toggleIntake(row, $event)"></nb-toggle></dd></div>
      <div><dt>{{ 'platform.overview.intake.col_month' | translate }}</dt><dd>{{ row.intake_month_mails != null ? formatNumber(row.intake_month_mails) : '-' }}</dd></div>
      <div><dt>{{ 'platform.overview.intake.col_held' | translate }}</dt><dd>{{ row.intake_held != null ? formatNumber(row.intake_held) : '-' }}</dd></div>
      <div><dt>{{ 'platform.overview.quotes.col_enabled' | translate }}</dt>
        <dd><nb-toggle [checked]="!!row.quotes_enabled" [disabled]="isIntakeBusy(row)" (checkedChange)="toggleQuotes(row, $event)"></nb-toggle></dd></div>
    </dl>
  </div>
</ng-template>

<ng-template #dash>-</ng-template>
